import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { ChevronLeft, Package } from 'lucide-react';
import { useTheme } from '../components/ThemeContext';
import { useLoLAContext } from '@nicherest/lola/training/LoLAContext';
import { useCart } from '../../../../../../context/CartContext';
import themeRegistry from '../registry.json';
import { iconMap } from '@nicherest/lola/agents/ui/shared/icons';
import Hero from '@nicherest/lola/agents/ui/hero';
import Products from '@nicherest/lola/agents/ui/products';
import { NextEvent, EventsList } from '@nicherest/lola/agents/ui/events';
import Contact from '@nicherest/lola/agents/ui/contact';
import SocialFollows from '@nicherest/lola/agents/ui/social-follows';
import MarketplaceLinks from '@nicherest/lola/agents/ui/marketplace-links';
import FooterBlock from '@nicherest/lola/agents/ui/footer';

const NICHEREST_NAV = [
  { label: 'Friendamals', href: '/friendamals' },
  { label: 'Craft Fairs', href: '/oh/cincinnati/craft-fairs' },
  { label: 'Businesses', href: '/oh/cincinnati/businesses' },
];

interface VendorBusinessPageProps {
  businessData?: any;
  businessName?: string;
  geo?: {
    country: string;
    state: string;
    region: string;
    metro: string;
    suburb?: string;
  };
}

const sectionComponents: Record<string, React.FC<any>> = {
  products: Products,
  upcomingEvents: NextEvent,
  events: EventsList,
  contact: Contact,
  socialFollows: SocialFollows,
  marketplaceLinks: MarketplaceLinks,
};

const VendorBusinessPage: React.FC<VendorBusinessPageProps> = ({ businessData, businessName, geo }) => {
  const { clientName: paramClientName } = useParams<{ clientName: string }>();
  const clientName = businessName || paramClientName;
  const { theme, refreshTheme, isLoading, error } = useTheme();
  const { setBusinessId, setPageContext, setAvailableAgents } = useLoLAContext();
  const { addItem } = useCart();
  const [craftFairEvents] = useState<any[]>([]);
  const [productCategories, setProductCategories] = useState<any[] | null>(null);
  const [refreshKey, setRefreshKey] = useState(0);

  useEffect(() => {
    if (clientName) {
      const profileTemplate = themeRegistry.templates.find((t: any) => t.name === 'profile');
      setBusinessId(clientName);
      setPageContext(profileTemplate?.name || 'profile');
      setAvailableAgents(profileTemplate?.availableAgents || []);
    }
    return () => {
      setBusinessId(null);
      setPageContext(null);
      setAvailableAgents(null);
    };
  }, [clientName, setBusinessId, setPageContext, setAvailableAgents]);

  useEffect(() => {
    const handleUpdate = () => {
      setRefreshKey(prev => prev + 1);
      if (clientName) refreshTheme(clientName);
    };
    window.addEventListener('lolaDataUpdate', handleUpdate);
    return () => window.removeEventListener('lolaDataUpdate', handleUpdate);
  }, [clientName, refreshTheme]);

  useEffect(() => {
    if (clientName && theme) {
      const localData = localStorage.getItem(`businesses_data_${clientName}`);
      if (localData) {
        try {
          const parsedData = JSON.parse(localData);
          if (parsedData?.business?.productCategories) {
            const localCount = parsedData.business.productCategories.length;
            const themeCount = theme.business?.productCategories?.length || 0;
            if (localCount < themeCount) {
              localStorage.removeItem(`businesses_data_${clientName}`);
              setProductCategories(theme.business?.productCategories || null);
              return;
            }
            setProductCategories([...parsedData.business.productCategories]);
            return;
          }
        } catch {
          // fall through to theme data
        }
      }
      setProductCategories(theme.business?.productCategories || null);
    }
  }, [theme, clientName, refreshKey]);

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setProductCategories(cats => cats); // trigger re-render (lightbox is inside Products)
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, []);

  if (isLoading) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Loading...</div>
      </div>
    );
  }

  if (error || !theme) {
    return (
      <div style={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
        <div>Business not found</div>
      </div>
    );
  }

  const business = theme.business || {} as any;
  const categories = productCategories ?? (theme.business?.productCategories || []);

  const getSectionOrder = () => {
    if (business.sectionOrder && Array.isArray(business.sectionOrder)) return business.sectionOrder;
    return [
      { key: 'products', visible: business?.visibility?.products !== false },
      { key: 'upcomingEvents', visible: business?.visibility?.upcomingEvents !== false },
      { key: 'events', visible: business?.visibility?.events !== false },
      { key: 'contact', visible: business?.visibility?.contact !== false },
      { key: 'socialFollows', visible: business?.visibility?.socialFollows !== false },
      { key: 'marketplaceLinks', visible: business?.visibility?.marketplaceLinks !== false },
    ];
  };

  const getSectionProps = (key: string): any => {
    switch (key) {
      case 'products':
        return {
          theme,
          categories,
          businessSlug: clientName || '',
          businessName: business.name || clientName || '',
          onAddToCart: addItem,
        };
      case 'upcomingEvents':
      case 'events':
        return { theme, events: craftFairEvents, geo };
      case 'contact':
        return { theme, business };
      case 'socialFollows':
        return { theme, social: business.social || {} };
      case 'marketplaceLinks':
        return { theme, marketplaces: business.marketplaces || {} };
      default:
        return {};
    }
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: theme.colors.surface,
      fontFamily: theme.fonts.body,
      color: theme.colors.text,
      position: 'relative'
    }}>
      <a
        href={geo ? `/${geo.state}/${geo.metro}/craft-fairs` : '/'}
        style={{
          position: 'fixed', top: '20px', left: '0',
          backgroundColor: theme.colors.background,
          padding: '12px 16px 12px 12px',
          borderRadius: '0 12px 12px 0', boxShadow: theme.shadows.md,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          textDecoration: 'none', color: theme.colors.primary, zIndex: 1000,
          transition: 'all 0.2s ease'
        }}
        onMouseOver={(e) => { e.currentTarget.style.transform = 'translateX(5px)'; e.currentTarget.style.boxShadow = theme.shadows.lg; }}
        onMouseOut={(e) => { e.currentTarget.style.transform = 'translateX(0)'; e.currentTarget.style.boxShadow = theme.shadows.md; }}
      >
        <ChevronLeft size={24} />
      </a>

      <Hero theme={theme} business={business} />

      <div style={{ maxWidth: '1200px', margin: '0 auto', padding: '60px 20px' }}>
        {getSectionOrder()
          .filter((s: any) => s.visible)
          .map((s: any) => {
            const Block = sectionComponents[s.key];
            if (!Block) return null;
            return <Block key={s.key} {...getSectionProps(s.key)} />;
          })}

        {business.products && business.products.length > 0 && theme.visibility?.products !== false && (
          <section style={{
            backgroundColor: theme.colors.background, borderRadius: theme.borderRadius.lg,
            boxShadow: theme.shadows.md, padding: '40px', marginBottom: '40px'
          }}>
            <h2 style={{ fontFamily: theme.fonts.heading, color: theme.colors.heading, fontSize: '2rem', margin: '0 0 30px 0', textAlign: 'center' }}>
              Products We Sell
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '30px' }}>
              {business.products.map((product: any, index: number) => {
                const IconComponent = (iconMap as Record<string, React.ComponentType<{ size?: number }>>)[product.icon] || Package;
                return (
                  <div key={index} style={{
                    backgroundColor: theme.colors.surface, borderRadius: theme.borderRadius.md,
                    padding: '25px', border: `1px solid ${theme.colors.border}`, textAlign: 'center'
                  }}>
                    <div style={{ marginBottom: '15px', display: 'flex', justifyContent: 'center', color: theme.colors.primary }}>
                      <IconComponent size={40} />
                    </div>
                    <h3 style={{ fontFamily: theme.fonts.heading, color: theme.colors.heading, margin: '0 0 15px 0' }}>{product.name}</h3>
                    <p style={{ color: theme.colors.text, lineHeight: '1.6', margin: '0' }}>{product.description}</p>
                  </div>
                );
              })}
            </div>
          </section>
        )}
      </div>

      <FooterBlock
        copyright="Â© 2025 Nicherest. Find your niche!"
        navLinks={NICHEREST_NAV}
      />

      <div style={{ backgroundColor: '#0f172a', color: '#94a3b8', padding: '20px', textAlign: 'center', fontSize: '0.9rem' }}>
        Powered by <strong style={{ color: '#ffffff' }}>lola</strong>
      </div>
    </div>
  );
};

export default VendorBusinessPage;
